/**
 * Select field component
 * Dropdown selection with static or dynamic options
 */

import React, { useMemo } from 'react';
import { FieldSchema, ValidationResult, SelectOption } from '@/config/fieldConfig';

export interface SelectFieldProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  appContext?: any;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
  appContext,
}) => {
  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const help = typeof schema.help === 'function' ? schema.help() : schema.help;

  // Get options from static list, app context, or current config
  const options = useMemo<SelectOption[]>(() => {
    // Static options
    if (schema.options) {
      return schema.options;
    }

    // Dynamic options source based on form state (e.g., switch between chatModels and visionModels)
    const dynamicOptionsSource = schema.getOptionsSource
      ? schema.getOptionsSource(appContext)
      : schema.optionsSource;

    // Dynamic options from app context (e.g., 'chatModels')
    if (dynamicOptionsSource && appContext) {
      const optionsArray = appContext[dynamicOptionsSource];
      if (Array.isArray(optionsArray)) {
        return optionsArray.map((option: any) => ({
          value: option.id || option.value,
          label: option.display_name || option.label || option.id || option.value,
        }));
      }
    }

    // Dynamic options from current config (e.g., 'output_schemas')
    if (schema.optionsFromConfig && appContext) {
      const configArray = appContext[schema.optionsFromConfig];
      if (Array.isArray(configArray)) {
        // Use custom mapper if provided, otherwise default mapping
        if (schema.optionsMapper) {
          return configArray.map(schema.optionsMapper);
        }
        return configArray.map((item: any) => ({
          value: item.key || item.id || item.value,
          label: item.name || item.label || item.key || item.id,
        }));
      }
    }

    return [];
  }, [schema.options, schema.optionsSource, schema.getOptionsSource, schema.optionsFromConfig, schema.optionsMapper, appContext]);

  // Get dynamic help text based on current value
  const currentHelp = useMemo(() => {
    if (typeof help === 'object' && help !== null && value) {
      return help[value] || help;
    }
    return help;
  }, [help, value]);

  const borderClass = error && !error.isWarning
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : error?.isWarning
    ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  return (
    <div className="field-wrapper">
      <label htmlFor={`field-${name}`} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {schema.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <select
        id={`field-${name}`}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 rounded-lg border
          focus:outline-none focus:ring-2
          disabled:bg-gray-100 disabled:cursor-not-allowed
          ${borderClass}
        `}
      >
        {/* Add empty option if allowEmpty is set or no value selected and not required */}
        {(schema.allowEmpty || ((value === undefined || value === null || value === '') && !schema.required)) && (
          <option value="">{schema.emptyLabel || '-- Select --'}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}

      {currentHelp && !error && (
        <div className="text-xs text-gray-500 mt-1">
          {typeof currentHelp === 'string' ? currentHelp : ''}
        </div>
      )}
    </div>
  );
};

export default SelectField;
